// src/pages/offlineSales/components/CustomerLocationPicker.jsx
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Search, X, MapPin, Crosshair, Loader2 } from 'lucide-react';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { reverseGeocodeLocation, resolveGoogleMapsLink } from '../../../services/api/location';
import useLocationSearch from '../../../hooks/useLocationSearch';
import styles from './CustomerLocationPicker.module.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const SRI_LANKA_CENTER = [7.8731, 80.7718];

const MapClickHandler = ({ onChange }) => {
  useMapEvents({
    click(e) {
      onChange({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
  });
  return null;
};

const MapRecenter = ({ value }) => {
  const map = useMapEvents({});
  useEffect(() => {
    if (value) {
      map.setView([value.latitude, value.longitude], Math.max(map.getZoom(), 15));
    }
  }, [value?.latitude, value?.longitude]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const CustomerLocationPicker = ({ value, onChange }) => {
  const { t } = useLocalization();
  const { error } = useAlert();

  const [nearbyAddress, setNearbyAddress] = useState(null);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [reverseError, setReverseError] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [googleLink, setGoogleLink] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  const {
    searchTerm,
    results,
    selectedIndex,
    showResults,
    loading: searchLoading,
    handleSearchChange,
    runSearch,
    handleKeyDown,
    selectResult,
    clearSearch,
    hideResults,
    showResultsDropdown,
  } = useLocationSearch();

  const reverseRequestRef = useRef(0);

  useEffect(() => {
    if (!value) {
      setNearbyAddress(null);
      setReverseError(false);
      return;
    }

    const requestId = ++reverseRequestRef.current;
    setReverseLoading(true);
    setReverseError(false);

    reverseGeocodeLocation(value.latitude, value.longitude)
      .then((data) => {
        if (reverseRequestRef.current !== requestId) return; // stale response, ignore
        setNearbyAddress(data?.displayName || null);
        if (!data?.displayName) setReverseError(true);
      })
      .catch(() => {
        if (reverseRequestRef.current !== requestId) return;
        setNearbyAddress(null);
        setReverseError(true);
      })
      .finally(() => {
        if (reverseRequestRef.current === requestId) setReverseLoading(false);
      });
  }, [value?.latitude, value?.longitude]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchResultSelect = (result) => {
    selectResult(result);
    onChange({ latitude: result.latitude, longitude: result.longitude });
  };

  const handleSearchKeyDownWrapper = (e) => {
    const selected = handleKeyDown(e);
    if (selected) handleSearchResultSelect(selected);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      error(t('offlineSales.locationNotSupported'));
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDetecting(false);
        onChange({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      },
      (err) => {
        setDetecting(false);
        if (err.code === 1) error(t('offlineSales.locationPermissionDenied'));
        else if (err.code === 2) error(t('offlineSales.locationUnavailable'));
        else if (err.code === 3) error(t('offlineSales.locationTimeout'));
        else error(t('offlineSales.locationUnavailable'));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleUseLink = async () => {
    const url = googleLink.trim();
    if (!url) return;
    setLinkLoading(true);
    try {
      const coords = await resolveGoogleMapsLink(url);
      onChange({ latitude: coords.latitude, longitude: coords.longitude });
      setGoogleLink('');
    } catch (err) {
      error(err?.response?.data?.message || t('offlineSales.locationGoogleLinkInvalid'));
    } finally {
      setLinkLoading(false);
    }
  };

  const handleClearLocation = () => {
    onChange(null);
  };

  return (
    <div className={styles.picker}>
      <div className={styles.citySearchWrapper}>
        <div className={styles.searchInputWrapper}>
          <input
            type="text"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
            onKeyDown={handleSearchKeyDownWrapper}
            onFocus={showResultsDropdown}
            onBlur={() => setTimeout(hideResults, 150)}
            placeholder={t('offlineSales.locationSearchPlaceholder')}
            className={styles.citySearchInput}
            autoComplete="off"
          />
          {searchTerm && (
            <button type="button" onClick={clearSearch} className={styles.clearCityBtn}>
              <X size={12} />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={runSearch}
          disabled={searchLoading || !searchTerm.trim()}
          className={styles.searchBtn}
        >
          {searchLoading ? <Loader2 size={14} className={styles.loadingIcon} /> : <Search size={14} />}
          {' '}{t('offlineSales.locationSearchButton')}
        </button>
        {showResults && (
          <div className={styles.cityDropdown}>
            {results.length === 0 ? (
              <div className={styles.searchEmpty}>{t('offlineSales.locationSearchNoResults')}</div>
            ) : (
              results.map((result, idx) => (
                <div
                  key={`${result.latitude}-${result.longitude}-${idx}`}
                  className={`${styles.cityOption} ${idx === selectedIndex ? styles.cityOptionSelected : ''}`}
                  onMouseDown={() => handleSearchResultSelect(result)}
                >
                  <div className={styles.cityOptionName}>{result.displayName}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <button type="button" onClick={handleUseMyLocation} disabled={detecting} className={styles.locationBtn}>
        <Crosshair size={14} /> {detecting ? t('offlineSales.locationDetecting') : t('offlineSales.locationUseMyLocation')}
      </button>

      <div className={styles.mapContainer}>
        <MapContainer
          center={value ? [value.latitude, value.longitude] : SRI_LANKA_CENTER}
          zoom={value ? 15 : 8}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MapClickHandler onChange={onChange} />
          <MapRecenter value={value} />
          {value && (
            <Marker
              position={[value.latitude, value.longitude]}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const latlng = e.target.getLatLng();
                  onChange({ latitude: latlng.lat, longitude: latlng.lng });
                },
              }}
            />
          )}
        </MapContainer>
      </div>

      {value && (
        <div className={styles.coordRow}>
          <div>
            <span className={styles.label}>{t('offlineSales.locationLatitude')}</span>
            <div className={styles.coordValue}>{value.latitude.toFixed(6)}</div>
          </div>
          <div>
            <span className={styles.label}>{t('offlineSales.locationLongitude')}</span>
            <div className={styles.coordValue}>{value.longitude.toFixed(6)}</div>
          </div>
          <button type="button" onClick={handleClearLocation} className={styles.clearLocationBtn}>
            <X size={12} /> {t('offlineSales.locationClear')}
          </button>
        </div>
      )}

      {value && (
        <div className={styles.nearbyAddress}>
          <MapPin size={12} />
          {reverseLoading
            ? t('offlineSales.locationDetecting')
            : reverseError
              ? t('offlineSales.locationNearbyAddressUnavailable')
              : `${t('offlineSales.locationNearbyAddress')}: ${nearbyAddress || '—'}`}
        </div>
      )}

      <div className={styles.divider}>
        <span>{t('common.or', 'OR')}</span>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('offlineSales.locationGoogleLinkLabel')}</label>
        <div className={styles.linkRow}>
          <input
            type="text"
            value={googleLink}
            onChange={e => setGoogleLink(e.target.value)}
            placeholder={t('offlineSales.locationGoogleLinkPlaceholder')}
            className={styles.input}
          />
          <button type="button" onClick={handleUseLink} disabled={linkLoading || !googleLink.trim()} className={styles.confirmBtn}>
            {t('offlineSales.locationUseLinkButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerLocationPicker;
